/**
 * Window geometry for the AVA Assistant: side-panel width and the floating drawer's position
 * and size. Every result is clamped so the assistant stays on screen and never gets smaller
 * than its minimum, including a size remembered from a bigger window.
 * Rects are { x, y, w, h } in pixels, relative to the bounds { width, height } they live in.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const PANEL_LIMITS = Object.freeze({ def: 440, min: 340, max: 760, minContent: 360, step: 16 });

export const DRAWER_LIMITS = Object.freeze({ def: Object.freeze({ w: 420, h: 720 }), minW: 360, minH: 420, margin: 8, inset: 24 });

/** Drag directions: edges and corners. */
export const RESIZE_DIRECTIONS = Object.freeze(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const rounded = ({ x, y, w, h }) => ({ x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });

/**
 * Side panel width, leaving at least PANEL_LIMITS.minContent for the page beside it.
 * @param {number} width
 * @param {number} containerWidth
 */
export function clampPanelWidth(width, containerWidth) {
  const max = Math.max(PANEL_LIMITS.min, Math.min(PANEL_LIMITS.max, containerWidth - PANEL_LIMITS.minContent));
  return Math.round(clamp(width, PANEL_LIMITS.min, max));
}

/** @param {{x:number,y:number,w:number,h:number}} r @param {{width:number,height:number}} bounds */
export function clampDrawerRect(r, bounds) {
  const m = DRAWER_LIMITS.margin;
  const w = clamp(r.w, DRAWER_LIMITS.minW, Math.max(DRAWER_LIMITS.minW, bounds.width - 2 * m));
  const h = clamp(r.h, DRAWER_LIMITS.minH, Math.max(DRAWER_LIMITS.minH, bounds.height - 2 * m));
  return rounded({ w, h, x: clamp(r.x, m, Math.max(m, bounds.width - w - m)), y: clamp(r.y, m, Math.max(m, bounds.height - h - m)) });
}

/** The drawer's first position: bottom-right, DRAWER_LIMITS.inset from the edges. */
export function defaultDrawerRect(bounds) {
  const { w, h } = DRAWER_LIMITS.def;
  const i = DRAWER_LIMITS.inset;
  const size = clampDrawerRect({ x: 0, y: 0, w, h }, bounds);
  return clampDrawerRect({ ...size, x: bounds.width - size.w - i, y: bounds.height - size.h - i }, bounds);
}

export function moveRect(r0, dx, dy, bounds) {
  return clampDrawerRect({ ...r0, x: r0.x + dx, y: r0.y + dy }, bounds);
}

/**
 * Resizes from an edge or corner; the opposite edges stay where they are.
 * @param {string} dir one of RESIZE_DIRECTIONS
 */
export function resizeRect(dir, r0, dx, dy, bounds) {
  const m = DRAWER_LIMITS.margin;
  let left = r0.x;
  let top = r0.y;
  let right = r0.x + r0.w;
  let bottom = r0.y + r0.h;
  if (dir.includes('e')) right = clamp(right + dx, left + DRAWER_LIMITS.minW, bounds.width - m);
  if (dir.includes('w')) left = clamp(left + dx, m, right - DRAWER_LIMITS.minW);
  if (dir.includes('s')) bottom = clamp(bottom + dy, top + DRAWER_LIMITS.minH, bounds.height - m);
  if (dir.includes('n')) top = clamp(top + dy, m, bottom - DRAWER_LIMITS.minH);
  const edges = { left: Math.round(left), top: Math.round(top), right: Math.round(right), bottom: Math.round(bottom) };
  return { x: edges.left, y: edges.top, w: edges.right - edges.left, h: edges.bottom - edges.top };
}
